import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, ChevronRight, Shield, Droplets, Hammer } from 'lucide-react';
import { Reveal, SplitText } from './Reveal';
import EngineeredStackDiagram from './EngineeredStackDiagram';

/**
 * GroundworksUSP — PREMIUM EDITORIAL (v3)
 * Magazine-grade layout: blueprint grid back-plate, Roman-numeral chapter
 * marker, credential pill row, large editorial headline, refined CTA, and
 * a true engineered cross-section diagram in place of the old bar list.
 */

const CREDS = [
  { Icon: Hammer,   label: 'Civil engineers' },
  { Icon: Shield,   label: 'Since 2004' },
  { Icon: Droplets, label: 'SUDS compliant' },
];

export default function GroundworksUSP() {
  return (
    <section
      data-testid="groundworks-usp"
      className="relative bg-[#162A1E] text-[#E7E9E4] py-32 md:py-48 px-5 md:px-12 overflow-hidden border-y border-[#E7E9E4]/8"
    >
      {/* Layered background — gradient + faint blueprint grid + glow + grain */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#162A1E] via-[#162A1E] to-[#162A1E]" />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(106,143,62,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(106,143,62,0.6) 1px, transparent 1px)',
          backgroundSize: '64px 64px, 64px 64px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 80%)',
        }}
      />
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-[#6A8F3E]/8 blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[#314F2B]/10 blur-[140px]" />
      <div className="absolute inset-0 grain opacity-15 hidden md:block" />

      <div className="mx-auto relative" style={{ maxWidth: '1600px' }}>
        {/* Roman-numeral chapter marker + label */}
        <div className="text-center mb-14 md:mb-20">
          <Reveal>
            <div className="inline-flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Wrench size={14} className="text-[#6A8F3E]" strokeWidth={2.2} />
                <span className="text-[10px] tracking-[0.4em] uppercase text-[#6A8F3E] font-bold">
                  Our Difference
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-x-0 gap-y-10 md:gap-16 items-center">
          {/* LEFT — editorial copy */}
          <div className="col-span-12 lg:col-span-6 min-w-0">
            <div className="relative">
              {/* Credential pill row */}
              <Reveal>
                <div className="flex flex-wrap gap-2 mb-8">
                  {CREDS.map(({ Icon, label }) => (
                    <span
                      key={label}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#6A8F3E]/30 bg-[#6A8F3E]/5 backdrop-blur-md"
                    >
                      <Icon size={12} className="text-[#6A8F3E]" strokeWidth={2.4} />
                      <span className="text-[10px] tracking-[0.25em] uppercase text-[#E7E9E4]/85 font-semibold">
                        {label}
                      </span>
                    </span>
                  ))}
                </div>
              </Reveal>

              <h2 className="font-serif-editorial text-4xl md:text-6xl lg:text-[5.4rem] leading-[1.08] tracking-tight mb-8 break-words">
                <SplitText text="Anyone can" />
                <span className="block mt-2">
                  <SplitText text="lay grass." delay={0.1} />
                </span>
                <span className="block italic font-light text-[#6A8F3E] mt-4 text-3xl md:text-5xl lg:text-6xl leading-[1.15]">
                  <SplitText text="We engineer what's underneath." delay={0.25} />
                </span>
              </h2>

              {/* Decorative serif ornament */}
              <Reveal delay={0.3}>
                <div className="flex items-center gap-3 mb-6 mt-2">
                  <span className="w-8 h-px bg-[#6A8F3E]/60" />
                  <span className="flex-1 h-px bg-[#E7E9E4]/8 max-w-[180px]" />
                </div>
              </Reveal>

              <Reveal delay={0.35}>
                <div className="space-y-4 text-base md:text-lg text-[#E7E9E4]/85 leading-relaxed max-w-xl">
                  <p>
                    Guardian is a <span className="text-[#6A8F3E] font-semibold">civil-engineering business first</span>. We come from a groundworks background — which means every lawn we install is built on properly engineered foundations, not just a bag of sand.
                  </p>
                  <p className="text-[#E7E9E4]/70">
                    Free-draining sub-base, weed-suppressing membrane, calibrated falls, SUDS-compliant drainage — the boring details that decide whether your lawn lasts 18 months or 18 years.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.45}>
                <Link
                  to="/installation"
                  data-testid="usp-method-link"
                  className="inline-flex items-center gap-2 mt-10 text-sm tracking-wide font-semibold text-[#6A8F3E] hover:text-[#E7E9E4] transition-colors group"
                >
                  <span className="border-b border-[#6A8F3E]/40 group-hover:border-[#E7E9E4] pb-0.5 transition-colors">SEE OUR 6-STEP METHOD</span>
                  <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
                </Link>
              </Reveal>
            </div>
          </div>

          {/* RIGHT — Premium architectural cross-section card */}
          <div className="col-span-12 lg:col-span-6 min-w-0">
            <Reveal delay={0.15}>
              <div
                data-testid="engineered-stack-card"
                className="relative rounded-3xl p-5 sm:p-7 md:p-9 min-w-0 overflow-hidden"
                style={{
                  backdropFilter: 'blur(24px) saturate(130%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(130%)',
                  background: 'linear-gradient(135deg, rgba(106,143,62,0.12), rgba(22,42,30,0.55))',
                  border: '1px solid rgba(106,143,62,0.25)',
                  boxShadow: '0 50px 120px -40px rgba(0,0,0,0.7), inset 0 1px 0 rgba(231,233,228,0.12)',
                }}
              >
                {/* corner crosshair marks — engineering-drawing flourish */}
                <CornerMarks />
                <EngineeredStackDiagram />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function CornerMarks() {
  const c = 'border-[#6A8F3E]/45';
  return (
    <div aria-hidden className="absolute inset-3 pointer-events-none">
      <span className={`absolute top-0 left-0 w-3 h-3 border-t border-l ${c}`} />
      <span className={`absolute top-0 right-0 w-3 h-3 border-t border-r ${c}`} />
      <span className={`absolute bottom-0 left-0 w-3 h-3 border-b border-l ${c}`} />
      <span className={`absolute bottom-0 right-0 w-3 h-3 border-b border-r ${c}`} />
    </div>
  );
}
